import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions as MuiDialogActions,
  IconButton,
  Box,
  Checkbox,
  Tooltip,
  CircularProgress,
  Alert,
  Chip,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import DeleteIcon from "@mui/icons-material/Delete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import axiosInstance from "API/axiosInstance";
import { API_ENDPOINTS } from "config/api";

const AdminUploadedAlumniDataDialog = ({ open, onClose, uploadedBy = null }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [moving, setMoving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteSingleId, setDeleteSingleId] = useState(null);

  useEffect(() => {
    if (open) {
      fetchData();
    } else {
      setSelectedIds(new Set());
      setMessage({ type: "", text: "" });
    }
  }, [open, uploadedBy]);

  const fetchData = async () => {
    setLoading(true);
    setMessage({ type: "", text: "" });
    try {
      const url = API_ENDPOINTS.ADMIN.GET_ALL_UPLOADED_ALUMNI_DATA(
        uploadedBy,
        false // Only show unprocessed records
      );
      const response = await axiosInstance.get(url);
      const dataArray = response.data || [];
      
      // Debug: Log first record to check property names
      if (dataArray.length > 0) {
        console.log("Sample uploaded alumni data:", dataArray[0]);
        console.log("Domain value:", dataArray[0].domain || dataArray[0].Domain);
        console.log("Category value:", dataArray[0].category || dataArray[0].Category);
      }
      
      setData(dataArray);
    } catch (error) {
      console.error("Error fetching uploaded alumni data:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to fetch uploaded alumni data.",
      });
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(new Set(data.map((item) => item.uploadId)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (uploadId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(uploadId)) {
      newSelected.delete(uploadId);
    } else {
      newSelected.add(uploadId);
    }
    setSelectedIds(newSelected);
  };

  const handleMoveSelected = async () => {
    if (selectedIds.size === 0) {
      setMessage({ type: "warning", text: "Please select at least one record to move." });
      return;
    }

    setMoving(true);
    setMessage({ type: "", text: "" });

    try {
      const response = await axiosInstance.post(API_ENDPOINTS.ADMIN.MOVE_SELECTED_TO_MASTER, {
        uploadIds: Array.from(selectedIds),
        userId: 1, // TODO: Get from auth context
      });

      setMessage({
        type: "success",
        text: response.data?.resultMsg || "Records moved to master successfully!",
      });

      // Refresh data
      await fetchData();
      setSelectedIds(new Set());
    } catch (error) {
      console.error("Error moving records to master:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to move records to master.",
      });
    } finally {
      setMoving(false);
    }
  };

  const handleMoveSingle = async (uploadId) => {
    setMoving(true);
    setMessage({ type: "", text: "" });

    try {
      const response = await axiosInstance.post(API_ENDPOINTS.ADMIN.MOVE_SELECTED_TO_MASTER, {
        uploadIds: [uploadId],
        userId: 1, // TODO: Get from auth context
      });

      setMessage({
        type: "success",
        text: response.data?.resultMsg || "Record moved to master successfully!",
      });

      // Refresh data
      await fetchData();
    } catch (error) {
      console.error("Error moving record to master:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to move record to master.",
      });
    } finally {
      setMoving(false);
    }
  };

  const handleDeleteSingle = (uploadId) => {
    setDeleteSingleId(uploadId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteBulk = () => {
    if (selectedIds.size === 0) {
      setMessage({ type: "warning", text: "Please select at least one record to delete." });
      return;
    }
    setDeleteSingleId(null);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    setMessage({ type: "", text: "" });
    setDeleteConfirmOpen(false);

    try {
      const idsToDelete = deleteSingleId ? [deleteSingleId] : Array.from(selectedIds);

      const response = await axiosInstance.post(API_ENDPOINTS.ADMIN.DELETE_UPLOADED_ALUMNI_DATA, {
        uploadIds: idsToDelete,
        deletedBy: 1, // TODO: Get from auth context
      });

      setMessage({
        type: "success",
        text: response.data?.message || `${response.data?.deletedCount || 0} record(s) deleted successfully!`,
      });

      // Refresh data
      await fetchData();
      setSelectedIds(new Set());
      setDeleteSingleId(null);
    } catch (error) {
      console.error("Error deleting records:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to delete records.",
      });
    } finally {
      setDeleting(false);
    }
  };

  const tableData = useMemo(() => {
    const columns = [
      {
        Header: (
          <Checkbox
            checked={data.length > 0 && selectedIds.size === data.length}
            indeterminate={selectedIds.size > 0 && selectedIds.size < data.length}
            onChange={handleSelectAll}
            icon={<CheckBoxOutlineBlankIcon />}
            checkedIcon={<CheckBoxIcon />}
          />
        ),
        accessor: "select",
        width: "5%",
        align: "center",
      },
      { Header: "S.No", accessor: "sNo", width: "5%", align: "left" },
      { Header: "Name", accessor: "name", width: "15%", align: "left" },
      { Header: "Email", accessor: "emailID", width: "15%", align: "left" },
      { Header: "Contact", accessor: "contactNumber", width: "10%", align: "left" },
      { Header: "WhatsApp", accessor: "whatsappNumber", width: "10%", align: "left" },
      { Header: "Batch", accessor: "batchYear", width: "8%", align: "left" },
      { Header: "Domain", accessor: "domain", width: "10%", align: "left" },
      { Header: "Category", accessor: "category", width: "10%", align: "left" },
      { Header: "Designation", accessor: "designation", width: "12%", align: "left" },
      { Header: "Location", accessor: "location", width: "10%", align: "left" },
      { Header: "Uploaded Date", accessor: "uploadedDate", width: "10%", align: "left" },
      { Header: "Actions", accessor: "action", width: "10%", align: "center" },
    ];

    const rows = data.map((item) => ({
      select: (
        <Checkbox
          checked={selectedIds.has(item.uploadId)}
          onChange={() => handleSelectOne(item.uploadId)}
          icon={<CheckBoxOutlineBlankIcon />}
          checkedIcon={<CheckBoxIcon />}
        />
      ),
      sNo: (
        <MDTypography variant="caption" fontWeight="medium">
          {item.sNo || "-"}
        </MDTypography>
      ),
      name: (
        <MDTypography variant="caption" fontWeight="medium">
          {item.name || "-"}
        </MDTypography>
      ),
      emailID: (
        <MDTypography variant="caption" color="text">
          {item.emailID || "-"}
        </MDTypography>
      ),
      contactNumber: (
        <MDTypography variant="caption" color="text">
          {item.contactNumber || "-"}
        </MDTypography>
      ),
      whatsappNumber: (
        <MDTypography variant="caption" color="text">
          {item.whatsappNumber || "-"}
        </MDTypography>
      ),
      batchYear: (
        <MDTypography variant="caption" color="text">
          {item.batchYear || "-"}
        </MDTypography>
      ),
      domain: (
        <MDTypography variant="caption" color="text">
          {item.domain || item.Domain || "-"}
        </MDTypography>
      ),
      category: (
        <MDTypography variant="caption" color="text">
          {item.category || item.Category || "-"}
        </MDTypography>
      ),
      designation: (
        <MDTypography variant="caption" color="text">
          {item.designation || "-"}
        </MDTypography>
      ),
      location: (
        <MDTypography variant="caption" color="text">
          {item.location || "-"}
        </MDTypography>
      ),
      uploadedDate: (
        <MDTypography variant="caption" color="text">
          {item.uploadedDate
            ? new Date(item.uploadedDate).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "-"}
        </MDTypography>
      ),
      action: (
        <Box display="flex" gap={0.5} alignItems="center">
          <Tooltip title="Move to Master">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleMoveSingle(item.uploadId)}
              disabled={moving || deleting || item.isProcessed}
            >
              <SyncAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteSingle(item.uploadId)}
              disabled={moving || deleting || item.isProcessed}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    }));

    return { columns, rows };
  }, [data, selectedIds, moving]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" fullScreen>
      <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}>
        <IconButton onClick={onClose} color="error">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <SyncAltIcon color="primary" />
            <MDTypography variant="h4" fontWeight="bold">
              Uploaded Alumni Data
            </MDTypography>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Chip
              label={`${selectedIds.size} selected`}
              color="primary"
              variant={selectedIds.size > 0 ? "filled" : "outlined"}
            />
            <MDButton
              variant="gradient"
              color="error"
              startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
              onClick={handleDeleteBulk}
              disabled={selectedIds.size === 0 || deleting || moving}
            >
              {deleting ? "Deleting..." : `Delete Selected (${selectedIds.size})`}
            </MDButton>
            <MDButton
              variant="gradient"
              color="success"
              startIcon={moving ? <CircularProgress size={18} color="inherit" /> : <SyncAltIcon />}
              onClick={handleMoveSelected}
              disabled={selectedIds.size === 0 || moving || deleting}
            >
              {moving ? "Moving..." : `Move Selected (${selectedIds.size})`}
            </MDButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {message.text && (
          <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
            {message.text}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : data.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <MDTypography variant="h6" color="text.secondary">
              No uploaded alumni data found.
            </MDTypography>
          </Box>
        ) : (
          <DataTable
            table={tableData}
            canSearch={true}
            entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
            showTotalEntries={true}
            pagination={{ variant: "gradient", color: "info" }}
            isSorted={true}
            noEndBorder={false}
          />
        )}
      </DialogContent>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text.secondary">
            {deleteSingleId
              ? "Are you sure you want to delete this record? This action cannot be undone."
              : `Are you sure you want to delete ${selectedIds.size} selected record(s)? This action cannot be undone.`}
          </MDTypography>
        </DialogContent>
        <MuiDialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </MuiDialogActions>
      </Dialog>
    </Dialog>
  );
};

AdminUploadedAlumniDataDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  uploadedBy: PropTypes.number,
};

export default AdminUploadedAlumniDataDialog;






